import { Box, Button } from '@mui/material';
import {
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useEffect,
} from 'react';
import { observer } from 'mobx-react';
import { useServiceModule } from '@product-framework/components/ServiceModuleProvider';
import FormHandler from './FormHandler';
import FieldGenerator from './FieldGenerator';
import { useLocalization } from '../../providers/LocalizationProvider';
import { useStore } from '../../providers/MobxProvider';
import getDefaultValuesForForm from './lib/getDefaultValuesForForm';
import { fetchAndCacheSearchSchema } from './lib/fetchAndCacheSearchSchema';

function FormSearchButtons({
  onSubmitClick,
  onHideClick,
  onClearClick,
  IconLibrary = {},
}) {
  const { formatMessage } = useLocalization();
  return (
    <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
      <Button
        variant="contained"
        startIcon={<IconLibrary.Search />}
        color="primary"
        onClick={() => onSubmitClick()}>
        {formatMessage({
          id: 'SEARCH_FIND_BUTTON',
          defaultMessage: 'Search',
        })}
      </Button>

      <Button
        variant="contained"
        color="primary"
        startIcon={<IconLibrary.ClearSearch />}
        onClick={() => {
          onClearClick();
        }}>
        {formatMessage({
          id: 'SEARCH_CLEAR_BUTTON',
          defaultMessage: 'Clear',
        })}
      </Button>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<IconLibrary.HideSearch />}
        onClick={() => onHideClick()}>
        {formatMessage({
          id: 'SEARCH_HIDE_BUTTON',
          defaultMessage: 'Hide',
        })}
      </Button>
    </Box>
  );
}

const FormSearchAdvanced = forwardRef(
  (
    {
      schema,
      schemaApiUrl,
      dataProvider,
      onClear = undefined,
      onSubmit = undefined,
      hideSearchForm = undefined,
      validationSchema = null,
      customComponents = {},
      IconLibrary = {},
      initialValues,
      children,
      storeName,
      getValueByControl = null,
      noLabels = false,
    },
    ref,
  ) => {
    const formRef = useRef();
    const store = useStore();
    const { moduleSettings = {} } = useServiceModule();

    if (!storeName) {
      throw Error('Store Name Not provided');
    }

    const { filters = [] } = store.domain.list.get(storeName);
    const cachedSchema =
      store.domain.list.searchSchemas?.[storeName] ??
      store.domain.list.getSearchSchema(storeName);

    const resolvedSchema = useMemo(() => {
      const base = schema || cachedSchema || [];
      if (!Array.isArray(base)) {
        return [];
      }
      return base.filter(
        field =>
          !field?.hasPermission || moduleSettings[field.hasPermission] === true,
      );
    }, [schema, cachedSchema, moduleSettings]);

    useImperativeHandle(ref, () => ({
      submit: () => {
        if (formRef.current) {
          formRef.current.submit();
        }
      },
      reset: () => {
        if (formRef.current) {
          formRef.current.reset();
        }
      },
    }));

    useEffect(() => {
      fetchAndCacheSearchSchema({
        store,
        storeName,
        schemaApiUrl,
        get: dataProvider?.get,
        schema,
      });
    }, [schema, schemaApiUrl, storeName, dataProvider, store]);

    const defaultInitialValuesFromSchema = getDefaultValuesForForm({
      schema: resolvedSchema,
      ...(getValueByControl && { getValueByControl }),
    });

    const defaultvalues = useMemo(() => {
      return filters?.reduce(
        (acc, filter) => ({
          ...acc,
          [filter?.field]: filter?.value,
        }),
        { ...defaultInitialValuesFromSchema, ...initialValues },
      );
    }, [filters, defaultInitialValuesFromSchema, initialValues]);

    return (
      <Box>
        <FormHandler
          isSearchForm
          formSchema={resolvedSchema}
          disableUnSavedCheck
          ref={formRef}
          validationSchema={validationSchema}
          initialValues={defaultvalues || {}}
          onSubmit={onSubmit}>
          <div className="form-fields">
            <div className="form-field-container">
              <FieldGenerator
                customComponents={customComponents}
                schema={resolvedSchema}
                noLabels={noLabels}
              />
            </div>
            {children ? (
              <div className="form-field-buttons">{children}</div>
            ) : (
              <FormSearchButtons
                IconLibrary={IconLibrary}
                formRef={formRef}
                onClearClick={() => {
                  onClear();
                  formRef.current.reset();
                }}
                onSubmitClick={() => formRef.current.submit()}
                onHideClick={() => {
                  if (hideSearchForm) {
                    hideSearchForm();
                  }
                }}
              />
            )}
          </div>
        </FormHandler>
      </Box>
    );
  },
);

export default observer(FormSearchAdvanced);
